import React, {useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faProcedures,
  faCapsules,
  faMoneyBillWave,
  faChartLine,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { urls } from "./config.dev";
import { ClipLoader } from "react-spinners";
import SettingsModal from './SettingsModal';
import SMSSettingsModal from './SMSSettingsModal';
import InactivePatientsModal from './InactivePatientsModal';

ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employeeName, setEmployeeName] = useState("");
  const [token, setToken] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clinicSessionToken, setClinicSessionToken] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [district, setDistrict] = useState("");
  const chartRef = useRef(null);
  const [showModal2, setShowModal2] = useState(false);
  const [showModal3, setShowModal3] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    employees: 0,
    activePatients: 0,
    inactivePatients: 0,
    lostClients: 0,
    drugsWorth: 0,
    dispensaryWorth: 0,
    stockWorth: 0,
  });
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get("token");

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get("token");
        console.log("URL Token:", tokenFromUrl);

        const securityResponse = await fetch(urls.security, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();

          if (securityData.message === "Session valid") {
            setEmployeeName(securityData.employee_name);
            setClinicSessionToken(securityData.clinic_session_token);
            setClinicName(securityData.clinic);
            setDistrict(securityData.district);
            fetchAdminDashboardData(tokenFromUrl);
          } else if (securityData.error === "Session expired") {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate("/login");
          }
        } else {
          throw new Error("Failed to perform security check");
        }
      } catch (error) {
        console.error("Error performing security check:", error);
        navigate("/login");
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  const fetchAdminDashboardData = async (token) => {
    try {
      const response = await fetch(urls.fetchAdminData, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Admin Dashboard Data:", data);

        setDashboardData({
          employees: data.employee_count,
          activePatients: data.active_patients_count,
          inactivePatients: data.inactive_patients_count,
          lostClients: data.lost_clients_count,
          drugsWorth: Math.round(data.total_worth),
          dispensaryWorth: Math.round(data.dispensary_worth),
          stockWorth: Math.round(data.stock_worth),
          revenue: Math.round(data.revenue),
        });
        setLoading(false);
      } else {
        throw new Error("Failed to fetch admin data");
      }
    } catch (error) {
      console.error("Error fetching admin dashboard data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenFromUrl) {
      fetchAdminDashboardData(tokenFromUrl);
    } else {
      console.error("Token is missing in the URL");
      setLoading(false);
    }
  }, [tokenFromUrl]);

  const handleChartClick = (event) => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');

    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log("URL Token:", tokenFromUrl);
    }

    if (!chartRef.current) return;

    const chartInstance = chartRef.current;
    const activePoints = chartInstance.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      true
    );

    if (activePoints.length > 0) {
      const datasetIndex = activePoints[0].datasetIndex;
      const dataIndex = activePoints[0].index;

      if (datasetIndex === 0 && dataIndex === 1) {
        setShowModal3(true);
      }
    }
  };

  const patientData = {
    labels: ["Active Patients", "Inactive Patients", "Lost Clients"],
    datasets: [
      {
        data: [
          dashboardData.activePatients,
          dashboardData.inactivePatients,
          dashboardData.lostClients,
        ],
        backgroundColor: ["#38a169", "#ed8936", "#f56565"],
        borderWidth: 1,
      },
    ],
  };

  const drugStockData = {
    labels: ["Dispensary Worth", "Store Worth"],
    datasets: [
      {
        data: [dashboardData.dispensaryWorth, dashboardData.stockWorth],
        backgroundColor: ["#00C49F", "#FFBB28"],
        borderWidth: 1,
      },
    ],
  };

  const handleShowModal = () => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log('URL Token:', tokenFromUrl);
    }
    setShowModal(true);
  };

  const handleShowModal2 = () => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log('URL Token:', tokenFromUrl);
    }
    setShowModal2(true);
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh",
        backgroundColor: "#f8fafc"
      }}>
        <ClipLoader size={50} color="#4f46e5" />
      </div>
    );
  }

  return (
    <div style={{ 
      display: "flex", 
      minHeight: "100vh", 
      backgroundColor: "#f8fafc",
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Sidebar */}
      <aside style={{
        width: "240px",
        backgroundColor: "#ffffff",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        position: "fixed",
        height: "100vh",
        zIndex: 10
      }}>
        {/* Clinic Name Display */}
        <div style={{
          fontSize: "18px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "24px",
          textAlign: "center",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "16px",
          textTransform: "uppercase",
          letterSpacing: "0.5px"
        }}>
          {clinicName}
        </div>
        
        <div style={{
          fontSize: "14px",
          color: "#64748b",
          marginBottom: "24px",
          textAlign: "center",
          fontStyle: "italic"
        }}>
          {district}
        </div>
        
        <nav style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {[
          
        
            { icon: faCog, label: "Settings", subMenu: ["Change Passwords", "SMS Settings", "Employee Settings"] },
          ].map((item, index) => (
            <div key={index} style={{ display: "flex", flexDirection: "column" }}>
              <a
                href="#"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  color: "#334155",
                  textDecoration: "none",
                  padding: "12px 16px",
                  borderRadius: "0px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  backgroundColor: item.label === "Dashboard" ? "#e0e7ff" : "transparent",
                  ":hover": {
                    backgroundColor: "#f1f5f9"
                  }
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = item.label === "Dashboard" ? "#e0e7ff" : "transparent")}
              >
                <FontAwesomeIcon icon={item.icon} style={{ width: "20px", color: "#4f46e5" }} />
                <span>{item.label}</span>
              </a>

              {/* SubMenu */}
              {item.subMenu.length > 0 && (
                <div style={{ marginLeft: "28px", marginTop: "4px", fontSize: "14px" }}>
                  {item.subMenu.map((subItem, subIndex) => (
                    <a
                      key={subIndex}
                      href="#"
                      style={{
                        display: "block",
                        color: "#475569",
                        textDecoration: "none",
                        padding: "8px 12px",
                        borderRadius: "0px",
                        transition: "all 0.2s ease",
                        fontWeight: "400",
                        ":hover": {
                          backgroundColor: "#f1f5f9",
                          color: "#4f46e5"
                        }
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                        e.currentTarget.style.color = "#4f46e5";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#475569";
                      }}
                     onClick={() => {
  if (subItem === "Employee Settings") {
    navigate(`/employeesettings?token=${tokenFromUrl}`);
  } else if (subItem === "Change Passwords") {  // Changed to plural
    handleShowModal();
  } else if (subItem === "SMS Settings") {
    handleShowModal2();
  }
}}
                    >
                      {subItem}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        
        {/* User Profile Section */}
        <div style={{
          marginTop: "auto",
          paddingTop: "24px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <div style={{
            width: "40px",
            height: "40px",
            borderRadius: "0%",
            backgroundColor: "#e0e7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#4f46e5",
            fontWeight: "600"
          }}>
            {employeeName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: "500", color: "#1e293b" }}>{employeeName}</div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>Administrator</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ 
        flex: 1, 
        padding: "32px", 
        marginLeft: "240px",
        maxWidth: "calc(100% - 240px)"
      }}>
        
         

        {/* Metrics Section */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", 
          gap: "24px",
          marginBottom: "32px"
        }}>
          {[
            { icon: faUsers, label: "Employees", value: dashboardData.employees, color: "#4f46e5" },
            { icon: faProcedures, label: "Active Patients", value: dashboardData.activePatients, color: "#f59e0b" },
            { icon: faCapsules, label: "Drugs Worth", value: `UGX ${dashboardData.drugsWorth.toLocaleString()}`, color: "#10b981" },
            { icon: faMoneyBillWave, label: "Current Month Revenue", value: `UGX ${dashboardData.revenue.toLocaleString()}`, color: "#f59e0b" }
          ].map((metric, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "0px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                ":hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                }
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px"
              }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "0px",
                  backgroundColor: `${metric.color}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <FontAwesomeIcon icon={metric.icon} style={{ 
                    fontSize: "20px", 
                    color: metric.color 
                  }} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: "14px", 
                    fontWeight: "500", 
                    color: "#64748b",
                    margin: "0 0 4px 0"
                  }}>
                    {metric.label}
                  </h3>
                  <p style={{ 
                    fontSize: "24px", 
                    fontWeight: "600", 
                    color: "#1e293b",
                    margin: 0
                  }}>
                    {metric.value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
          marginBottom: "32px"
        }}>
          {/* Patient Distribution Chart */}
          <div style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "0px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
          }}>
            <h3 style={{ 
              textAlign: "center", 
              fontSize: "16px", 
              fontWeight: "600",
              color: "#1e293b", 
              marginBottom: "24px"
            }}>
              Patient Distribution
            </h3>
            <div style={{ height: "300px" }}>
              <Doughnut 
                ref={chartRef} 
                data={patientData} 
                onClick={handleChartClick}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 20,
                        usePointStyle: true,
                        pointStyle: 'circle'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
          
          {/* Drug Stock Worth Chart */}
          <div style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "0px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
          }}>
            <h3 style={{ 
              textAlign: "center", 
              fontSize: "16px", 
              fontWeight: "600",
              color: "#1e293b", 
              marginBottom: "24px"
            }}>
              Drug Stock Worth
            </h3>
            <div style={{ height: "300px" }}>
              <Doughnut 
                data={drugStockData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 20,
                        usePointStyle: true,
                        pointStyle: 'circle'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Key Section */}
        <div style={{
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "0px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
        }}>
          <h3 style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
            marginTop: "0",
            marginBottom: "16px"
          }}>
            Patient Classification Key
          </h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px"
          }}>
            <div style={{
              padding: "16px",
              backgroundColor: "#f8fafc",
              borderRadius: "0px",
             
            }}>
              <h4 style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 8px 0"
              }}>
                Active Clients
              </h4>
              <p style={{
                fontSize: "13px",
                color: "#64748b",
                margin: "0",
                lineHeight: "1.5"
              }}>
                Patients who have visited the clinic multiple times within the last 6 months, or those who had a single visit within the last 4 months.
              </p>
            </div>
            
            <div style={{
              padding: "16px",
              backgroundColor: "#f8fafc",
              borderRadius: "0px",
           
            }}>
              <h4 style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 8px 0"
              }}>
                Inactive Clients
              </h4>
              <p style={{
                fontSize: "13px",
                color: "#64748b",
                margin: "0",
                lineHeight: "1.5"
              }}>
                Patients who visited the clinic between 6-12 months ago, or those who visited once within the past 4-8 months.
              </p>
            </div>
            
            <div style={{
              padding: "16px",
              backgroundColor: "#f8fafc",
              borderRadius: "0px",
             
            }}>
              <h4 style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 8px 0"
              }}>
                Lost Clients
              </h4>
              <p style={{
                fontSize: "13px",
                color: "#64748b",
                margin: "0",
                lineHeight: "1.5"
              }}>
                Patients who have not visited the clinic for over 12 months, either with a single or multiple visits in the past.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showModal && <SettingsModal token={token} onClose={() => setShowModal(false)} />}
      {showModal2 && <SMSSettingsModal token={token} onClose={() => setShowModal2(false)} />}
      {showModal3 && <InactivePatientsModal token={token} onClose={() => setShowModal3(false)} />}
    </div>
  );
};

export default AdminDashboard;